/**
 * Second-window work order view (#3653).
 *
 * A foreman keeps a list on one screen and a work order window on the other.
 * The list window posts `open` on a BroadcastChannel; the window that follows
 * (named WORK_ORDER_WINDOW_NAME by window.open) navigates and answers `ack`.
 * No ack in time means no live follower, and the click navigates in place.
 *
 * While following, the window also reports `viewing`: the work order its own
 * route shows, so the list window can highlight it (#3744).
 *
 * Every message carries user + tenant so another login in the same browser
 * profile never moves this user's window. BroadcastChannel only — without it
 * the feature is hidden and clicks behave as before.
 */

export const WORK_ORDER_WINDOW_CHANNEL = 'os-buddy-work-order-window';
export const WORK_ORDER_WINDOW_NAME = 'os-buddy-work-order-window';
export const WORK_ORDER_WINDOW_PATH = '/work-order-window';
/** Set while a follower window has following switched off, so its toggle stays visible. */
export const WORK_ORDER_WINDOW_PAUSED_KEY = 'os-buddy-work-order-window-paused';
export const WORK_ORDER_WINDOW_ACK_TIMEOUT_MS = 300;

export type WorkOrderWindowScope = {
  userId: string;
  tenantId: string;
};

export type WorkOrderWindowMessage = WorkOrderWindowScope & (
  | { type: 'probe' }
  | { type: 'hello'; windowId: string }
  | { type: 'bye'; windowId: string }
  | { type: 'open'; requestId: string; workOrderId: string }
  | { type: 'ack'; requestId: string; windowId: string }
  | { type: 'changed' }
  | { type: 'viewing'; windowId: string; workOrderId: string | null }
);

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

export function parseWorkOrderWindowMessage(value: unknown): WorkOrderWindowMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const { userId, tenantId } = record;
  if (!isNonEmptyString(userId) || !isNonEmptyString(tenantId)) return null;
  const scope = { userId, tenantId };

  switch (record.type) {
    case 'probe':
    case 'changed':
      return { ...scope, type: record.type };
    case 'hello':
    case 'bye':
      return isNonEmptyString(record.windowId)
        ? { ...scope, type: record.type, windowId: record.windowId }
        : null;
    case 'open':
      return isNonEmptyString(record.requestId) && isNonEmptyString(record.workOrderId)
        ? { ...scope, type: 'open', requestId: record.requestId, workOrderId: record.workOrderId }
        : null;
    case 'ack':
      return isNonEmptyString(record.requestId) && isNonEmptyString(record.windowId)
        ? { ...scope, type: 'ack', requestId: record.requestId, windowId: record.windowId }
        : null;
    case 'viewing':
      return isNonEmptyString(record.windowId)
        && (record.workOrderId === null || isNonEmptyString(record.workOrderId))
        ? { ...scope, type: 'viewing', windowId: record.windowId, workOrderId: record.workOrderId }
        : null;
    default:
      return null;
  }
}

export function isSameScope(a: WorkOrderWindowScope, b: WorkOrderWindowScope): boolean {
  return a.userId === b.userId && a.tenantId === b.tenantId;
}

export function isWorkOrderWindowSupported(): boolean {
  return typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';
}

/** True in the window opened by the list's "open second window" button while it follows. */
export function isFollowingWindow(): boolean {
  return typeof window !== 'undefined' && window.name === WORK_ORDER_WINDOW_NAME;
}

function readPaused(): boolean {
  try {
    return sessionStorage.getItem(WORK_ORDER_WINDOW_PAUSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** A follower window, following or paused — the windows that show the follow toggle. */
export function isWorkOrderWindow(): boolean {
  return isFollowingWindow() || readPaused();
}

/**
 * Pausing clears window.name, so the list's window.open no longer targets this
 * window either; resuming takes the name back.
 */
export function setWindowFollowing(following: boolean): void {
  window.name = following ? WORK_ORDER_WINDOW_NAME : '';
  try {
    if (following) sessionStorage.removeItem(WORK_ORDER_WINDOW_PAUSED_KEY);
    else sessionStorage.setItem(WORK_ORDER_WINDOW_PAUSED_KEY, '1');
  } catch {
    // Blocked storage: the toggle disappears on reload, following stays off.
  }
}

export function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

// One channel per page: a BroadcastChannel never delivers to the object that
// posted, so this window does not hear its own hello/open.
let sharedChannel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (sharedChannel !== null) return sharedChannel;
  if (!isWorkOrderWindowSupported()) return null;
  try {
    sharedChannel = new BroadcastChannel(WORK_ORDER_WINDOW_CHANNEL);
  } catch {
    return null;
  }
  return sharedChannel;
}

export function postWorkOrderWindowMessage(message: WorkOrderWindowMessage): void {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // A closed or unsupported channel: the ack timeout falls back to in-place navigation.
  }
}

export function subscribeToWorkOrderWindow(
  onMessage: (message: WorkOrderWindowMessage) => void,
): () => void {
  const channel = getChannel();
  if (channel === null) return () => {};
  const handleMessage = (event: MessageEvent<unknown>) => {
    const parsed = parseWorkOrderWindowMessage(event.data);
    if (parsed !== null) onMessage(parsed);
  };
  channel.addEventListener('message', handleMessage);
  return () => {
    channel.removeEventListener('message', handleMessage);
  };
}

/** Test seam: drop the page channel so the next call builds a fresh one. */
export function resetWorkOrderWindowChannel(): void {
  sharedChannel?.close();
  sharedChannel = null;
}

/** The part of a Window Management API screen we place by (#3654). */
export type ScreenArea = {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
};

type ScreenDetailsLike = {
  screens: readonly ScreenArea[];
  currentScreen: ScreenArea;
};

// Chromium only and not in lib.dom yet: feature-detect, never assume.
type WindowWithScreenDetails = Window & { getScreenDetails?: () => Promise<ScreenDetailsLike> };
type ScreenWithExtended = Screen & { isExtended?: boolean };

function sameArea(a: ScreenArea, b: ScreenArea): boolean {
  return a.availLeft === b.availLeft && a.availTop === b.availTop
    && a.availWidth === b.availWidth && a.availHeight === b.availHeight;
}

/** A screen other than the one the list window is on, or null with a single screen. */
export function pickOtherScreen(details: ScreenDetailsLike): ScreenArea | null {
  const { currentScreen } = details;
  return details.screens.find((s) => s !== currentScreen && !sameArea(s, currentScreen)) ?? null;
}

/**
 * The screen to open the popup on (#3654, #3729), or null to let the browser
 * place it. Asked only when the desk has more than one screen
 * (`screen.isExtended`), so a single-screen user never sees the permission
 * prompt. Must run before window.open: once the popup takes focus, Chrome
 * anchors the prompt to the list window behind it and nobody sees it.
 */
export async function findOtherScreen(): Promise<ScreenArea | null> {
  const getScreenDetails = (window as WindowWithScreenDetails).getScreenDetails;
  if ((window.screen as ScreenWithExtended).isExtended !== true || typeof getScreenDetails !== 'function') {
    return null;
  }
  try {
    return pickOtherScreen(await getScreenDetails.call(window));
  } catch {
    // Denied: the popup opens where the browser puts it.
    return null;
  }
}

/**
 * Opens (or reuses) the follower window as a separate popup, on the other
 * screen where the browser allows. Null when the browser blocked the popup,
 * e.g. the permission prompt outlasted the click's user activation; the
 * permission is settled by then, so the next click opens straight away.
 */
export async function openWorkOrderWindow(path: string = WORK_ORDER_WINDOW_PATH): Promise<Window | null> {
  const target = await findOtherScreen();
  const area = target === null
    ? {
        width: Math.round(window.screen.availWidth * 0.6),
        height: Math.round(window.screen.availHeight * 0.85),
      }
    : { left: target.availLeft, top: target.availTop, width: target.availWidth, height: target.availHeight };
  const features = Object.entries(area).map(([key, value]) => `${key}=${String(value)}`);
  return window.open(path, WORK_ORDER_WINDOW_NAME, ['popup=yes', ...features].join(','));
}
